import styled from 'styled-components';

import { Card } from '@components/card';
import { HtmlContent } from '@components/html-content';
import { Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useEmailSettings } from 'api/company/hooks';
import { useActionEmailTemplate } from 'api/journeys/hooks';

import { AttachmentList } from '../common';
export const usePreviewEmailModal = () => useModal(PreviewEmail, { variant: 'LARGE' });

const EMPTY_EMAIL = '<p></p>';

export type PreviewEmailProps = {
  action: 'journey' | 'traveller';
  shortId: string;
  to: string | string[];
  content?: string;
  subject?: string;
  attachments?: string[];
};

type EmailItemProps = {
  infoText: string;
  text: string;
};

export const PreviewEmail = (props: PreviewEmailProps) => {
  const { to, subject, shortId, action, attachments } = props;
  const { t } = useScopedTranslate(locale);
  const { data } = useActionEmailTemplate(action, shortId);

  const recipient = Array.isArray(to) && to.length > 0 ? to.join(', ') : t('noRecipient');
  const subjectInfo = subject ? subject : t('noSubject');
  const emailSettingsQuery = useEmailSettings();
  const emailSettings = emailSettingsQuery.data;
  const emailAttachments = attachments ?? [];

  return (
    <Modal>
      <Modal.SimpleHeader title={t('emailPreview')} />
      <ContentContainer>
        <Modal.Content>
          <Stack space={8}>
            <Stack space={2}>
              <Stack space={1}>
                <EmailItem
                  infoText={t('from')}
                  text={t('email', {
                    name: emailSettings?.fromName ?? '50skills',
                    email: emailSettings?.fromAddress ?? 'noreply@50skills.com',
                  })}
                />
                <Separator />
              </Stack>
              <Stack space={1}>
                <EmailItem infoText={t('to')} text={recipient} />
                <Separator />
              </Stack>
              <Stack space={1}>
                <EmailItem infoText={t('subject')} text={subjectInfo} />
                {emailAttachments.length > 0 && <Separator />}
              </Stack>

              {emailAttachments.length > 0 && (
                <Stack space={4} align="top" orientation="horizontal">
                  <StyledText color="textSecondary" font="semibold14" text={t('attachments')} />
                  <Stack space={1}>
                    <AttachmentList attachments={emailAttachments} />
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Card space={1}>
              <HtmlContent font="regular14" html={data || EMPTY_EMAIL} />
            </Card>
          </Stack>
        </Modal.Content>
      </ContentContainer>
    </Modal>
  );
};

const EmailItem = (props: EmailItemProps) => {
  const { text, infoText } = props;
  return (
    <Stack space={4} align="center" orientation="horizontal">
      <StyledText color="textSecondary" font="semibold14" text={infoText} />
      <Text font="regular14" text={text} />
    </Stack>
  );
};

const ContentContainer = styled.div({
  overflowY: 'auto',
  marginTop: pxToRem(24),
});

const StyledText = styled(Text)({
  width: pxToRem(120),
});

const locale = {
  en: {
    emailPreview: 'Email preview',
    from: 'FROM',
    to: 'TO',
    subject: 'SUBJECT',
    poweredBy: 'Powered by 50skills',
    email: '{name} <{email}>',
    noContent: 'Email has no content',
    noSubject: 'Email has no subject',
    noRecipient: 'Email has no recipients',
    attachments: 'ATTACHMENTS',
  },
};
